import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getVideoById, getVideosByCategory, videos } from '@/mocks/videos';
import { getArticleById, getArticlesByCategory } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import type { Video } from '@/mocks/videos';
import type { Article } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const numId = Number(id);
  const video: Video | undefined = getVideoById(numId);
  const article: Article | undefined = getArticleById(numId);

  const content = video || article;

  if (!content || isNaN(numId)) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <p className="text-foreground-500 text-lg">内容不存在</p>
          <Link to="/" className="text-primary-400 hover:text-primary-300 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isVideo = 'duration' in content;
  const categorySlug = content.category;
  const category = categoryMap[categorySlug];
  const relatedVideos = isVideo
    ? getVideosByCategory(categorySlug).filter((v) => v.id !== numId).slice(0, 4)
    : [];
  const relatedArticles = getArticlesByCategory(categorySlug).filter((a) => a.id !== numId).slice(0, 3);

  const jsonLd = isVideo
    ? {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: (content as Video).title,
        description: (content as Video).description,
        image: (content as Video).thumbnail,
        url: `https://www.51video.com/article/${numId}`,
        dateCreated: (content as Video).updateDate,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: (content as Video).rating,
          bestRating: 10,
        },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: (content as Article).title,
        description: (content as Article).excerpt,
        image: (content as Article).coverImage,
        url: `https://www.51video.com/article/${numId}`,
        author: { '@type': 'Person', name: (content as Article).author },
        datePublished: (content as Article).publishDate,
      };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6 lg:px-8 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category?.name || content.category, href: `/category/${categorySlug}` },
                  { label: 'title' in content && isVideo ? (content as Video).title : (content as Article).title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <article className="lg:col-span-2">
                  {isVideo ? (
                    <>
                      {/* Video Player Placeholder */}
                      <div className="relative aspect-video rounded-xl overflow-hidden bg-black mb-6">
                        <img
                          src={(content as Video).thumbnail}
                          alt={(content as Video).title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 cursor-pointer transition-all hover:scale-110">
                            <i className="ri-play-fill text-background-50 text-3xl md:text-4xl"></i>
                          </div>
                        </div>
                        <div className="absolute bottom-3 right-3 bg-black/70 text-foreground-200 text-xs px-2 py-1 rounded">
                          {(content as Video).duration}
                        </div>
                      </div>

                      <h1 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-3">
                        {(content as Video).title}
                      </h1>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {(content as Video).views} 次观看
                        </span>
                        <span className="flex items-center gap-1 text-accent-500">
                          <i className="ri-star-fill"></i> {(content as Video).rating} 分
                        </span>
                        <span>{(content as Video).year}年</span>
                        <span>更新于 {(content as Video).updateDate}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {(content as Video).tags.map((tag) => (
                          <span key={tag} className="px-3 py-1 rounded-full bg-background-800 text-foreground-500 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="prose prose-invert max-w-none">
                        <p className="text-foreground-400 text-sm leading-relaxed">{(content as Video).description}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={(content as Article).coverImage}
                        alt={(content as Article).title}
                        className="w-full aspect-video rounded-xl object-cover mb-6"
                      />
                      <h1 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-3">
                        {(content as Article).title}
                      </h1>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i> {(content as Article).author}
                        </span>
                        <span>{(content as Article).publishDate}</span>
                        <span>{(content as Article).views} 阅读</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {(content as Article).tags.map((tag) => (
                          <span key={tag} className="px-3 py-1 rounded-full bg-background-800 text-foreground-500 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div
                        className="prose prose-invert max-w-none text-foreground-400 text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: (content as Article).content }}
                      />
                    </>
                  )}
                </article>

                {/* Sidebar */}
                <aside className="lg:col-span-1">
                  {isVideo && relatedVideos.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-foreground-200 font-semibold text-sm mb-4 font-heading">相关推荐</h3>
                      <div className="space-y-3">
                        {relatedVideos.map((v) => (
                          <Link
                            key={v.id}
                            to={`/article/${v.id}`}
                            className="flex gap-3 group cursor-pointer"
                          >
                            <div className="relative w-28 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                              <img
                                src={v.thumbnail}
                                alt={v.title}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute bottom-1 right-1 bg-black/70 text-foreground-300 text-[10px] px-1 rounded">
                                {v.duration}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-foreground-300 text-xs line-clamp-2 group-hover:text-primary-400 transition-colors">
                                {v.title}
                              </h4>
                              <div className="flex items-center gap-2 mt-1 text-[11px] text-foreground-600">
                                <span>{v.views}</span>
                                <span className="text-accent-500">{v.rating}分</span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {relatedArticles.length > 0 && (
                    <div>
                      <h3 className="text-foreground-200 font-semibold text-sm mb-4 font-heading">相关资讯</h3>
                      <div className="space-y-3">
                        {relatedArticles.map((a) => (
                          <Link
                            key={a.id}
                            to={`/article/${a.id}`}
                            className="flex gap-3 group cursor-pointer"
                          >
                            <img
                              src={a.coverImage}
                              alt={a.title}
                              className="w-20 h-14 rounded-lg object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-foreground-300 text-xs line-clamp-2 group-hover:text-primary-400 transition-colors">
                                {a.title}
                              </h4>
                              <span className="text-[11px] text-foreground-600 mt-1 block">{a.publishDate}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}